/**
 * Container for the Text Notes `document_config.content` editor. Holds the
 * immutable update logic for body columns and the summary-resolution rules, and
 * renders the presentational cards. Required CMM stats fields are part of the
 * content form but are no longer exposed in the UI. Emits the whole updated
 * content form via onChange.
 */
import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../../components/queryBuilder/queryBuilderUtils";
import { QueryGroup } from "../../../../../data-model/queryBuilder";
import BodyColumnsTable from "./bodyColumnsTable";
import SummaryResolutionCard from "./summaryResolutionCard";
import {
  BodyColumnRow,
  SummaryRuleRow,
  TextNotesContentForm,
} from "./textNotesContentUtils";

interface TextNotesContentSectionProps {
  resetKey: number;
  value: TextNotesContentForm;
  onChange: (next: TextNotesContentForm) => void;
  /**
   * Deletes a body column. Owned by the parent because removing a column also
   * unselects it from the Routing table's "What we add".
   */
  onRemoveBodyColumn: (id: string) => void;
}

const TextNotesContentSection = ({
  resetKey,
  value,
  onChange,
  onRemoveBodyColumn,
}: TextNotesContentSectionProps) => {
  const patch = (changes: Partial<TextNotesContentForm>) =>
    onChange({ ...value, ...changes });

  const mapColumns = (fn: (c: BodyColumnRow) => BodyColumnRow) =>
    patch({ bodyColumns: value.bodyColumns.map(fn) });

  const mapRules = (fn: (r: SummaryRuleRow) => SummaryRuleRow) =>
    patch({ summaryRules: value.summaryRules.map(fn) });

  return (
    <>
      <BodyColumnsTable
        resetKey={resetKey}
        rows={value.bodyColumns}
        onAdd={() =>
          patch({
            bodyColumns: [
              ...value.bodyColumns,
              {
                id: uuidv4(),
                fieldName: "",
                displayLabel: "",
                formatTemplate: "",
                renderOrder: undefined,
                required: false,
                bqColumns: [],
                fallbackSelect: "",
                whatWeAddLabel: "",
              },
            ],
          })
        }
        onRemove={onRemoveBodyColumn}
        onCellChange={(id, key, cellValue) =>
          mapColumns((c) => {
            if (c.id !== id) return c;
            if (key === "renderOrder") {
              return {
                ...c,
                renderOrder: cellValue === "" ? undefined : Number(cellValue),
              };
            }
            const map: Record<string, keyof BodyColumnRow> = {
              fieldName: "fieldName",
              displayLabel: "displayLabel",
              formatTemplate: "formatTemplate",
              fallbackSelect: "fallbackSelect",
            };
            const field = map[key];
            return field ? { ...c, [field]: cellValue } : c;
          })
        }
        onRequiredChange={(id, required) =>
          mapColumns((c) => (c.id === id ? { ...c, required } : c))
        }
        onBqColumnsChange={(id, bqColumns) =>
          mapColumns((c) => (c.id === id ? { ...c, bqColumns } : c))
        }
      />

      {/* Required CMM Stats Fields card is intentionally not rendered; the
          stored `required_cmm_stats_fields` still round-trips through the form. */}

      <SummaryResolutionCard
        resetKey={resetKey}
        enabled={value.summaryEnabled}
        fieldName={value.summaryFieldName}
        sourceColumns={value.summarySourceColumns}
        rules={value.summaryRules}
        onEnabledChange={(summaryEnabled) => patch({ summaryEnabled })}
        onFieldNameChange={(summaryFieldName) => patch({ summaryFieldName })}
        onSourceColumnsChange={(summarySourceColumns) =>
          patch({ summarySourceColumns })
        }
        onAddRule={() =>
          patch({
            summaryRules: [
              ...value.summaryRules,
              {
                id: uuidv4(),
                ruleId: "",
                valueTemplate: "",
                when: createEmptyGroup(),
              },
            ],
          })
        }
        onRemoveRule={(id) =>
          patch({ summaryRules: value.summaryRules.filter((r) => r.id !== id) })
        }
        onRuleChange={(id, changes) =>
          mapRules((r) => (r.id === id ? { ...r, ...changes } : r))
        }
        onWhenChange={(id, when: QueryGroup) =>
          mapRules((r) => (r.id === id ? { ...r, when } : r))
        }
      />
    </>
  );
};

export default TextNotesContentSection;
